import { inject, Injectable, signal } from '@angular/core';
import { MyArvatoEnvironment } from '@myarvato/shared/models';
import { OverviewRecordDto } from '@myarvato/modules/transport/models';
import { TableLazyLoadEvent } from 'primeng/table';
import { AuthorizationService } from '@myarvato/core/authentication';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';
import { Observable } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class TransportExportRetrievalService {
  // injections
  private readonly authorizationService = inject(AuthorizationService);
  private readonly environment = inject(MyArvatoEnvironment);
  private readonly msalService = inject(MsalService);

  // fields
  private readonly baseUrl = `${this.environment.url.api}Transport/`;
  private readonly _averageBytesPerItem = 2500;

  private readonly _currentProgress = signal<number>(0);
  private readonly _estimatedMaxProgress = signal<number>(0);
  private readonly _tryGetToken = toSignal(this.tryGetAuthenticationDetails());

  public readonly currentProgress = this._currentProgress.asReadonly();
  public readonly estimatedMaxProgress = this._estimatedMaxProgress.asReadonly();

  public readonly aborted = signal<boolean>(false);

  public async getExportData(filter: TableLazyLoadEvent, numberOfItems: number): Promise<OverviewRecordDto[]> {
    this._currentProgress.set(0);
    this._estimatedMaxProgress.set(numberOfItems * this._averageBytesPerItem);

    // once we upgrade to Angular 20/21, we can use the resource API instead of fetch.
    // That supports using interceptors, so the manual header creation can be removed.
    const response = await fetch(`${this.baseUrl}Export`, {
      method: 'POST',
      body: JSON.stringify(filter),
      headers: this.getRequestOptions(),
    });

    const reader = response.body?.getReader();
    if (reader == null) {
      return [];
    }

    const decoder = new TextDecoder();
    let result = '';

    while (true) {
      const { done, value } = await reader.read();
      if (this.aborted()) {
        await reader.cancel();
        return [];
      }

      if (done) {
        break;
      }

      result += decoder.decode(value, { stream: true });
      this._currentProgress.set(result.length);
    }

    return JSON.parse(result);
  }

  private getRequestOptions(): HeadersInit {
    const client = this.authorizationService.selectedClientSettings();
    if (!client?.client) {
      throw new Error('No client settings found. Please ensure the user is logged in and has selected a client.');
    }

    const token = this._tryGetToken();
    if (!token?.accessToken) {
      throw new Error('No authentication token found. Please ensure the user is logged in.');
    }

    return {
      Accept: 'application/json',
      Authorization: `Bearer ${token.accessToken}`,
      'Content-Type': 'application/json',

      'x-skip-all-interceptor-handling': 'true',
      'x-error-message-target-key': 'none',
      'x-selectedclienthint': client.client,
    };
  }

  private tryGetAuthenticationDetails(): Observable<AuthenticationResult> {
    return this.msalService.acquireTokenSilent({
      account: this.msalService.instance.getActiveAccount() ?? undefined,
      scopes: [this.environment.auth.apiScope],
    });
  }
}
